import { FC, ReactNode } from 'react'

import clsx from 'clsx'

import { NavigationSlates, Network } from '@tribeplatform/gql-client/types'
import { Container } from '@tribeplatform/react-ui-kit/Layout'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { useSlateKit } from '@tribeplatform/slate-kit/hooks'
import { useQuery } from '@tribeplatform/slate-kit/utils'

import { useMobileSidebar } from '../../MobileSidebarProvider'
import { ErrorBoundary } from '../Error/ErrorBoundry'
import { PoweredByTribe } from '../PoweredByTribe'
import { layoutsAdditionalContext } from './constants'
import { SlateLayout } from './layout.enum'
import { SampleContent } from './sample-components/SampleContent.component'
import { SampleHeader } from './sample-components/SampleHeader.component'
import { SampleSidebar } from './sample-components/SampleSidebar.component'
import { getNavigationComponents } from './utils'

type LayoutProps = {
  navigationSlates: NavigationSlates
  layout: string
  hideContent?: boolean
  hideNavigation?: boolean
}

const withFooter = (Content: ReactNode, network: Network) => {
  const currentYear = new Date().getFullYear()
  return (
    <>
      {Content}
      <div className="flex flex-col space-y-3 mt-8 px-3">
        {(network?.termsOfServiceUrl || network?.privacyPolicyUrl) && (
          <div className="flex space-x-2">
            {network?.termsOfServiceUrl && (
              <Link
                variant="neutral"
                className="text-xs"
                rel="noopener noreferrer"
                href={network?.termsOfServiceUrl}
              >
                Terms of Service
              </Link>
            )}

            {network?.privacyPolicyUrl && (
              <Link
                rel="noopener noreferrer"
                variant="neutral"
                className="text-xs"
                href={network?.privacyPolicyUrl}
              >
                Privacy Policy
              </Link>
            )}
          </div>
        )}
        <div className="text-xs text-basicMain-600" aria-label="Copyright">
          © Copyright {currentYear}, {network?.name}
        </div>

        {network?.tribeBranding && (
          <Link
            rel="noopener noreferrer"
            variant="neutral"
            href={`https://tribe.so/?utm_campaign=powered-by&utm_medium=referral&utm_source=${network?.domain}&utm_term=${network?.name}`}
            target="_blank"
            className="text-basicMain-600 flex items-center -mt-1"
          >
            <PoweredByTribe />
          </Link>
        )}
      </div>
    </>
  )
}

export const Layout: FC<LayoutProps> = ({
  layout,
  navigationSlates,
  hideContent,
  hideNavigation,
  children,
}) => {
  const {
    context: { network },
  } = useSlateKit()
  const { mobileSidebarOpen } = useMobileSidebar()
  const queryParams = useQuery()

  const layoutSlug = (queryParams?.layout || layout) as SlateLayout
  const additionalContext = layoutsAdditionalContext[layoutSlug]
  const { Header, Sidebar1 } = getNavigationComponents(
    navigationSlates,
    additionalContext,
  )

  switch (layoutSlug) {
    case SlateLayout.CLASSIC:
      return (
        <div className="bg-main-50 min-h-screen">
          <ErrorBoundary>
            {hideNavigation ? <SampleHeader /> : Header}
          </ErrorBoundary>
          <div className="overflow-hidden pb-5 pt-5 sm:pt-7">
            <ErrorBoundary>
              {hideContent ? <SampleContent /> : children}
            </ErrorBoundary>
          </div>
        </div>
      )
    case SlateLayout.DEFAULT:
    default:
      return (
        <div className="bg-main-50 min-h-screen pb-10">
          <ErrorBoundary>
            {hideNavigation ? <SampleHeader /> : Header}
          </ErrorBoundary>
          <Container
            size="md"
            className={clsx(
              'w-full grid grid-cols-12 gap-5 overflow-hidden pb-5',
              mobileSidebarOpen && 'fixed',
            )}
          >
            <aside
              className="hidden lg:block lg:col-span-3 xl:col-span-3 pt-5 sm:pt-7"
              aria-label="Sidebar"
            >
              <ErrorBoundary>
                {hideNavigation ? (
                  <SampleSidebar />
                ) : (
                  withFooter(Sidebar1, network)
                )}
              </ErrorBoundary>
            </aside>
            <main className="col-span-12 lg:col-span-9 xl:col-span-9 pt-5 sm:pt-7">
              {hideContent ? (
                <SampleContent />
              ) : (
                <ErrorBoundary>{children}</ErrorBoundary>
              )}
            </main>
          </Container>
        </div>
      )
  }
}
